import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCardOrders } from '@/api/cardOrders';
import { filterOrdersBySearch } from '@/api/cardOrders/utils';
import TableFilter from '@/components/Table/TableFilter';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import CustomExpanderPanel from '@/features/administration/history/CustomExpanderPanel';
import OrderBody from '@/features/administration/history/OrderBody';
import OrderBodyDetails from '@/features/administration/history/OrderBodyDetails';
import OrderHeader from '@/features/administration/history/OrderHeader';
import OrderHistoryLoader from '@/features/administration/history/OrderHistoryLoader';
import OrderHistorySummary from '@/features/administration/history/OrderHistorySummary';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { t } from '@/lib/utils';

const OrderHistory = () => {
    const { locale } = useIntl();
    const { data, isLoading, isError, refetch } = useCardOrders();
    const { value: isCardOrderHistoryEnabled } = useFeatureToggle(FeatureToggles.ENABLE_CARD_ORDER_HISTORY);
    const [searchValue, setSearchValue] = useState('');

    const renderOrders = () => {
        if (!data?.cardOrderHistory || data.cardOrderHistory.length === 0) {
            return (
                <NotFoundState
                    headline={t('orderHistory.errorMessage.noOrdersFound.title')}
                    message={t('orderHistory.errorMessage.noOrdersFound.description')}
                    fullWidth={true}
                />
            );
        }

        const filteredOrders = filterOrdersBySearch(data.cardOrderHistory, searchValue);

        return (
            <>
                {data.historySummary && isCardOrderHistoryEnabled ? (
                    <OrderHistorySummary summary={data.historySummary} />
                ) : null}
                {isCardOrderHistoryEnabled ? (
                    <TableToolbar>
                        <div className='display-flex'>
                            <TableFilter
                                id='orderHistorySearch'
                                filter={searchValue}
                                setFilter={setSearchValue}
                                searchByLabel={t('orderHistory.filter.searchBy')}
                            />
                        </div>
                    </TableToolbar>
                ) : null}
                {filteredOrders.length === 0 ? (
                    <NotFoundState
                        headline={t('orderHistory.errorMessage.noOrdersFound.title')}
                        message={t('orderHistory.filter.noResults.description')}
                        fullWidth={true}
                    />
                ) : (
                    filteredOrders.map(order => (
                        <CustomExpanderPanel
                            key={order.orderId}
                            header={<OrderHeader orderId={order.orderId} />}
                            body={<OrderBody order={order} locale={locale} />}
                            bodyDetails={<OrderBodyDetails order={order} locale={locale} />}
                        />
                    ))
                )}
            </>
        );
    };

    if (isError) {
        return (
            <NotFoundState
                headline={t('orderHistory.errorMessage.unavailable.title')}
                message={t('orderHistory.errorMessage.unavailable.description')}
                buttons={[{ text: t('general.tryAgain'), onClick: () => refetch() }]}
                fullWidth={true}
            />
        );
    }

    return (
        <>
            {isLoading ? (
                <OrderHistoryLoader
                    showSummary={Boolean(isCardOrderHistoryEnabled)}
                    showFilter={Boolean(isCardOrderHistoryEnabled)}
                />
            ) : (
                renderOrders()
            )}
        </>
    );
};

export default OrderHistory;
